/**
 * Tea Radar — карта вкусов чая
 * 
 * Адаптировано из website с поддержкой светлой/тёмной темы
 */

import { useMemo } from 'react'
import type { TasteProfile } from '@/entities/product/model/types'

// Брендовые цвета с поддержкой тем
const brandColors = {
  // Красный акцент (бренд)
  red: '#C73B0D',
  redLight: '#E85A2C',
  // Кремовые тона (светлая тема)
  cream: '#FDF8F3',
  creamLight: '#FFFBF7',
  creamMid: '#F5EDE4',
  // Тёмная тема
  darkBg: '#1A1B1E',
  darkCard: '#2A2B2E',
  darkElevated: '#3A3B3E',
  // Серые для сетки
  gridLight: '#E8E2DC',
  gridDark: '#4A4B4E',
  // Текст
  text: '#5C4A3D',
  textDark: '#E5E5E5',
  textMuted: '#BEB0A3',
  textMutedDark: '#888888',
}

interface TeaRadarProps {
  data: TasteProfile[]
  className?: string
}

function TeaFlavorRadar({ data, isDark }: { data: TasteProfile[]; isDark: boolean }) {
  const width = 440
  const height = 440
  const centerX = width / 2
  const centerY = height / 2
  const maxRadius = 110
  const innerRadius = 20
  const levels = 5
  const labelPadding = 28

  // Создаем точки для многоугольника данных
  const dataPoints = useMemo(() => {
    return data.map((point, index) => {
      const angle = (index * 2 * Math.PI) / data.length - Math.PI / 2
      const value = Math.min(Math.max(point.value, 0), 10) / 10
      const dataRadius = innerRadius + (maxRadius - innerRadius) * value
      const x = centerX + Math.cos(angle) * dataRadius
      const y = centerY + Math.sin(angle) * dataRadius
      return { x, y, value: point.value, angle, name: point.name }
    })
  }, [data])

  // Создаём путь с дугами для нулевых значений
  const createRadarPath = () => {
    if (dataPoints.length === 0) return ''

    const n = dataPoints.length
    let path = `M ${dataPoints[0].x} ${dataPoints[0].y}`

    for (let i = 0; i < n; i++) {
      const curr = dataPoints[i]
      const next = dataPoints[(i + 1) % n]

      if (curr.value === 0 && next.value === 0) {
        path += ` A ${innerRadius} ${innerRadius} 0 0 1 ${next.x} ${next.y}`
      } else {
        path += ` L ${next.x} ${next.y}`
      }
    }

    return path
  }

  // Позиции для меток
  const labels = useMemo(() => {
    return data.map((point, index) => {
      const angle = (index * 2 * Math.PI) / data.length - Math.PI / 2
      const angleDeg = ((angle * 180) / Math.PI + 360) % 360

      const labelRadius = maxRadius + labelPadding
      const x = centerX + Math.cos(angle) * labelRadius
      const y = centerY + Math.sin(angle) * labelRadius

      let textAnchor: 'start' | 'middle' | 'end' = 'middle'
      let dy = 0

      if (angleDeg > 75 && angleDeg < 105) {
        dy = 12
      } else if (angleDeg > 255 && angleDeg < 285) {
        dy = -6
      } else if (angleDeg >= 105 && angleDeg <= 255) {
        textAnchor = 'end'
        dy = 3
      } else {
        textAnchor = 'start'
        dy = 3
      }

      const hasValue = point.value > 0

      return { x, y, textAnchor, dy, name: point.name, value: point.value, hasValue, angle }
    })
  }, [data])

  // Цвета в зависимости от темы
  const colors = {
    ringFill: isDark ? [brandColors.darkElevated, brandColors.darkCard] : [brandColors.creamMid, brandColors.creamLight],
    grid: isDark ? brandColors.gridDark : brandColors.gridLight,
    text: isDark ? brandColors.textDark : brandColors.text,
    textMuted: isDark ? brandColors.textMutedDark : brandColors.textMuted,
    gradientStart: isDark ? 'rgba(199, 59, 13, 0.3)' : 'rgba(235, 186, 168, 0.5)',
    gradientEnd: isDark ? 'rgba(199, 59, 13, 0.6)' : 'rgba(224, 160, 144, 0.9)',
  }

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-full" preserveAspectRatio="xMidYMid meet">
      <defs>
        {/* Градиент заливки */}
        <radialGradient id={`dataGradient-${isDark ? 'dark' : 'light'}`} cx="50%" cy="50%" r="70%">
          <stop offset="0%" stopColor={colors.gradientStart} />
          <stop offset="100%" stopColor={colors.gradientEnd} />
        </radialGradient>
      </defs>

      {/* Чередующиеся кольца */}
      {Array.from({ length: levels }, (_, i) => {
        const outerR = innerRadius + ((maxRadius - innerRadius) / levels) * (levels - i)
        const isEven = i % 2 === 0
        const fillColor = isEven ? colors.ringFill[0] : colors.ringFill[1]
        return <circle key={`ring-${i}`} cx={centerX} cy={centerY} r={outerR} fill={fillColor} />
      })}

      {/* Оси */}
      {data.map((point, index) => {
        const angle = (index * 2 * Math.PI) / data.length - Math.PI / 2
        const x1 = centerX + Math.cos(angle) * innerRadius
        const y1 = centerY + Math.sin(angle) * innerRadius
        const x2 = centerX + Math.cos(angle) * (maxRadius + labelPadding - 8)
        const y2 = centerY + Math.sin(angle) * (maxRadius + labelPadding - 8)
        const hasValue = point.value > 0
        return (
          <g key={index}>
            <line
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke={colors.grid}
              strokeWidth={hasValue ? '1' : '0.5'}
              opacity={hasValue ? 0.7 : 0.35}
            />
            {hasValue && <circle cx={x2} cy={y2} r={1.5} fill={colors.grid} opacity={0.8} />}
          </g>
        )
      })}

      {/* Многоугольник данных */}
      <path
        d={createRadarPath()}
        fill={`url(#dataGradient-${isDark ? 'dark' : 'light'})`}
        stroke={brandColors.red}
        strokeWidth="2"
        strokeLinejoin="round"
        strokeLinecap="round"
      />

      {/* Центральный круг */}
      <circle cx={centerX} cy={centerY} r={innerRadius} fill={isDark ? brandColors.darkBg : brandColors.cream} />
      <circle
        cx={centerX}
        cy={centerY}
        r={innerRadius - 2}
        fill="none"
        stroke={brandColors.red}
        strokeWidth="1.5"
        opacity="0.3"
      />

      {/* Точки на вершинах */}
      {dataPoints.map(
        (point, index) =>
          point.value > 0 && (
            <g key={index}>
              <circle cx={point.x} cy={point.y} r="3" fill={brandColors.red} opacity="0.2" />
              <circle cx={point.x} cy={point.y} r="2" fill={brandColors.red} />
            </g>
          )
      )}

      {/* Метки */}
      {labels.map((label, index) => (
        <text
          key={index}
          x={label.x}
          y={label.y + label.dy}
          textAnchor={label.textAnchor}
          dominantBaseline="middle"
          className="select-none"
          style={{
            fontSize: label.hasValue ? '10px' : '9px',
            fontWeight: label.hasValue ? 600 : 400,
            fill: label.hasValue ? colors.text : colors.textMuted,
            letterSpacing: '0.02em',
          }}
        >
          {label.name}
        </text>
      ))}
    </svg>
  )
}

export function TeaRadar({ data, className }: TeaRadarProps) {
  // Определяем тему по наличию класса dark на document
  const isDark = typeof document !== 'undefined' && document.documentElement.classList.contains('dark')

  if (!data || data.length === 0) {
    return (
      <div className={`flex flex-col items-center justify-center py-8 text-gray-400 dark:text-gray-500 ${className}`}>
        <svg className="w-10 h-10 mb-2 opacity-30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <circle cx="12" cy="12" r="10" />
          <path d="M12 6v6l4 2" />
        </svg>
        <span className="text-sm">Нет данных о вкусе</span>
      </div>
    )
  }

  return (
    <div className={`w-full flex items-center justify-center ${className}`}>
      <div className="w-full max-w-[340px] aspect-square">
        <TeaFlavorRadar data={data} isDark={isDark} />
      </div>
    </div>
  )
}

export default TeaRadar

